import React, { useState, useEffect, useCallback } from 'react';
import { FolderTree, Plus, Eye, MoreHorizontal, Layers, Trash2, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/auth';

interface CollectionItem {
  id: string;
  handle: string;
  title: string;
  productCount?: number;
  product_count?: number;
  published?: boolean;
  updated_at?: string;
  updatedAt?: string;
}

export default function CollectionsPage() {
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [newTitle, setNewTitle]       = useState('');
  const [newHandle, setNewHandle]     = useState('');
  const [submitting, setSubmitting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: CollectionItem[] }>('/collections');
      setCollections(res.data || []);
    } catch (err) {
      console.error('Failed to load collections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle) return;
    setSubmitting(true);
    try {
      await apiFetch('/collections', {
        method: 'POST',
        body: JSON.stringify({
          title: newTitle,
          handle: newHandle || undefined,
        }),
      });
      setNewTitle('');
      setNewHandle('');
      setShowModal(false);
      load();
    } catch (err) {
      console.error('Failed to create collection:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this collection?')) return;
    try {
      await apiFetch(`/collections/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      console.error('Failed to delete collection:', err);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Collections & Departments</h1>
          <p style={styles.subtitle}>Curate department groupings and seasonal drops</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowModal(true)}>
          <Plus size={16} />
          <span>New Collection</span>
        </button>
      </div>

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Collection</th>
              <th style={styles.th}>Slug / Handle</th>
              <th style={styles.th}>Products</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Last Modified</th>
              <th style={styles.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--admin-text-subtle)' }}><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /></td></tr>
            ) : collections.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--admin-text-subtle)' }}>No collections found. Create your first collection above.</td></tr>
            ) : collections.map((col) => (
              <tr key={col.id} style={styles.tr}>
                <td style={styles.td}>
                  <div style={styles.colTitleWrap}>
                    <Layers size={16} style={{ color: 'var(--admin-text-subtle)' }} />
                    <strong style={styles.colTitle}>{col.title}</strong>
                  </div>
                </td>
                <td style={styles.tdMono}>/{col.handle}</td>
                <td style={styles.td}>{col.product_count ?? col.productCount ?? 0} pieces</td>
                <td style={styles.td}>
                  <span style={styles.badgeActive}>
                    Published
                  </span>
                </td>
                <td style={styles.tdMuted}>{col.updated_at ? new Date(col.updated_at).toLocaleDateString('en-IN') : 'Recent'}</td>
                <td style={styles.tdRight}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <a
                      href={`/collections/${col.handle}`}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.iconBtn}
                      title="View on Storefront"
                    >
                      <Eye size={15} />
                    </a>
                    <button
                      type="button"
                      onClick={() => handleDelete(col.id)}
                      style={{ ...styles.iconBtn, color: '#EF4444' }}
                      title="Delete Collection"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Create Collection</h2>
            <form onSubmit={handleCreate} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Collection Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Summer Solstice Capsule"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>URL Handle</label>
                <input
                  type="text"
                  placeholder="e.g. summer-solstice"
                  value={newHandle}
                  onChange={(e) => setNewHandle(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => setShowModal(false)} style={styles.btnSecondary}>
                  Cancel
                </button>
                <button type="submit" style={styles.btnPrimary}>
                  Save Collection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' },
  title: { fontSize: '1.75rem', fontWeight: 600, color: '#FAF7F2', margin: 0 },
  subtitle: { fontSize: '0.875rem', color: '#9B9B9B', margin: '0.25rem 0 0 0' },
  btnPrimary: {
    background: '#C9A84C',
    color: '#1A1A1A',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  btnSecondary: {
    background: 'transparent',
    color: '#FAF7F2',
    border: '1px solid #3A3A3A',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  tableCard: {
    background: '#1F1F1F',
    border: '1px solid #2C2C2C',
    borderRadius: '8px',
    overflow: 'hidden',
    overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' },
  th: { padding: '0.875rem 1.25rem', background: '#161616', color: '#9B9B9B', fontWeight: 500, borderBottom: '1px solid #2C2C2C' },
  thRight: { padding: '0.875rem 1.25rem', background: '#161616', color: '#9B9B9B', fontWeight: 500, textAlign: 'right', borderBottom: '1px solid #2C2C2C' },
  tr: { borderBottom: '1px solid #2A2A2A' },
  td: { padding: '1rem 1.25rem', color: '#FAF7F2' },
  tdMono: { padding: '1rem 1.25rem', color: '#9B9B9B', fontFamily: 'monospace', fontSize: '0.8125rem' },
  tdMuted: { padding: '1rem 1.25rem', color: '#6B6B6B' },
  tdRight: { padding: '1rem 1.25rem', textAlign: 'right' },
  colTitleWrap: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  colTitle: { color: '#FAF7F2' },
  badgeActive: { background: '#163326', color: '#4ADE80', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' },
  badgeDraft: { background: '#2E2E2E', color: '#9B9B9B', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' },
  iconBtn: { background: 'transparent', border: 'none', color: '#9B9B9B', cursor: 'pointer', padding: '0.35rem', display: 'inline-flex' },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modal: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '95vw',
    maxWidth: '440px',
  },
  modalTitle: { margin: '0 0 1.25rem 0', color: '#FAF7F2', fontSize: '1.25rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.75rem', color: '#9B9B9B', fontWeight: 500 },
  input: {
    background: '#141414',
    border: '1px solid #333',
    borderRadius: '4px',
    padding: '0.6rem 0.75rem',
    color: '#FAF7F2',
    fontSize: '0.875rem',
  },
  modalButtons: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.75rem' },
};
